import React, { useMemo } from 'react';

import { Button, Center, Loader, Table, px, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import PayRateSelect from 'components/form-fields/select-fields/PayRateSelect';
import useOtherWorkPayRateLookups from 'hooks/otherWorkPayRateLookup/useOtherWorkPayRateLookups';
import useUpdateOtherWorkPayRateLookup from 'hooks/otherWorkPayRateLookup/useUpdateOtherWorkPayRateLookup';
import useOtherWorkTypes from 'hooks/otherWorkType/useOtherWorkTypes';
import useAllPages from 'hooks/useAllPages';
import { OtherWorkPayRateLookup } from 'types/generated/models/OtherWorkPayRateLookup';
import { getOtherWorkTypeToPayRateLookup } from 'utils/payRates.helpers';

import ModalCreateOtherWorkType from './components/ModalCreateOtherWorkType';
import OtherWorkTypeTableHeader from './components/OtherWorkTypeTableHeader';

interface IOtherWorkPayRateLookupTableProps {
  permission?: boolean;
  staff: string;
}

const OtherWorkPayRateLookupTable = ({
  staff,
  permission = true,
}: IOtherWorkPayRateLookupTableProps): JSX.Element => {
  const theme = useMantineTheme();
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const updateMutation = useUpdateOtherWorkPayRateLookup();
  const {
    items: lookups,
    isLoading: payRateLookupsLoading,
    isSuccess: payRateLookupsSuccess,
  } = useAllPages(useOtherWorkPayRateLookups({ staff, limit: '100' }));
  const {
    items: otherWorkTypes,
    isLoading: otherWorkTypesLoading,
    isSuccess: otherWorkTypesSuccess,
  } = useAllPages(useOtherWorkTypes({ limit: '100' }));

  const otherWorkTypeToPayRateLookup = useMemo(() => getOtherWorkTypeToPayRateLookup(lookups), [lookups]);

  const handleChangePayRate = (payRateId: string | null, values: OtherWorkPayRateLookup): void => {
    if (!!values) {
      const payload = {
        ...values,
        payRate: payRateId,
      };
      updateMutation.mutate(payload);
    }
  };

  return (
    <>
      {(payRateLookupsLoading || otherWorkTypesLoading) && (
        <Center my='xl'>
          <Loader />
        </Center>
      )}
      {payRateLookupsSuccess && otherWorkTypesSuccess && (
        <>
          <Table withColumnBorders withBorder sx={{ width: 'fit-content' }} verticalSpacing='md'>
            <thead>
              <tr>
                <th style={{ width: `${px(theme.spacing.xl) * 10}px`, background: theme.colors.gray[3] }}>
                  Other Rates
                </th>
                <th style={{ width: `${px(theme.spacing.xl) * 10}px`, background: theme.colors.gray[1] }}>
                  Hourly Rates
                </th>
              </tr>
            </thead>
            <tbody>
              {otherWorkTypes.map((otherWorkType, i) => (
                <tr key={otherWorkType.otherWorkTypeId}>
                  <OtherWorkTypeTableHeader otherWorkType={otherWorkType} permission={permission} />
                  <td style={{ width: `${px(theme.spacing.xl) * 10}px` }}>
                    <PayRateSelect
                      cyData={`otherWorkPayRateSelect${i}`}
                      payRateTypes={['HOURLY']}
                      disabled={!permission}
                      value={otherWorkTypeToPayRateLookup[`${otherWorkType.otherWorkTypeId}`]?.payRate}
                      onChange={event =>
                        handleChangePayRate(
                          event,
                          otherWorkTypeToPayRateLookup[`${otherWorkType.otherWorkTypeId}`],
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Button
            leftIcon={<IconPlus size={theme.fontSizes.sm} />}
            w='fit-content'
            onClick={openCreateModal}
            disabled={!permission}
            data-cy='createOtherWorkTypeButton'
          >
            Create Other Work Type
          </Button>
        </>
      )}
      <ModalCreateOtherWorkType opened={createModalOpened} handleClose={closeCreateModal} />
    </>
  );
};

export default OtherWorkPayRateLookupTable;
